/**
 * <SalesKpiRow summary={hub.summary} trend={hub.trend} compareLabel="vs previous 30 days" metric="revenue"
 *              onMetric={setMetric} onStock={() => navigate('/stock-health')} loading={loading} hasListFilter={false} />
 *
 * Five KPI cards for the Sales overview:
 *   Revenue, Units, Returns (units + return rate)  - clicking switches the main chart metric (aria-pressed).
 *                Revenue also shows "after returns" once returns are deducted from Amazon API sales
 *                (summary.returned_value / revenue_after_returns; targets stay on gross revenue).
 *   Target       achievement % for the period, actual of target, and the month-end pace
 *                ("On pace for ₹X of ₹Y in October") from summary.target_projection / target_month_revenue.
 *                No target: "No target set" with a link to /targets (or a hint when a brand/category/product
 *                filter switches targets off).
 *   Stock cover  revenue-weighted days of cover for the period's best-sellers + "N best-sellers under 15 days";
 *                clicking opens Stock health.
 * Also exports MetricInfo (small (i) tooltip button) for other sales views.
 */
import React from 'react';
import { Link } from 'react-router-dom';
import { Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { KpiCard } from './KpiCard';
import { coverTone, number, shortMoney, wholeMoney } from './format';

export const MetricInfo = ({ text, id }) => <TooltipProvider><Tooltip><TooltipTrigger asChild>
  <button type="button" className="inline-flex text-gray-500 hover:text-gray-800" aria-label={text} data-testid={`${id}-info`}><Info className="h-3.5 w-3.5" aria-hidden="true" /></button>
</TooltipTrigger><TooltipContent className="max-w-xs text-xs" data-testid={`${id}-explanation`}>{text}</TooltipContent></Tooltip></TooltipProvider>;

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const monthName = ym => (ym && /^\d{4}-\d{2}$/.test(ym) ? MONTH_NAMES[Number(ym.slice(5, 7)) - 1] : 'the month');
const pct = value => (value == null || Number.isNaN(Number(value)) ? '—' : `${Number(value).toFixed(1)}%`);
const COVER_TEXT = { danger: 'text-red-700', warning: 'text-amber-700', success: 'text-gray-900', neutral: 'text-gray-900' };

const TargetFootnote = ({ s, hasListFilter }) => {
  if (s.target_revenue == null) {
    if (hasListFilter) return <span>Targets are set per platform. Clear brand, category and product filters to see progress.</span>;
    return <Link to="/targets" className="font-medium text-blue-700 hover:underline" data-testid="sales-set-target">Set a monthly target →</Link>;
  }
  const achieved = Math.max(0, Math.min(100, Number(s.target_achievement) || 0));
  const onTrack = s.target_projection != null && s.target_month_revenue ? s.target_projection >= s.target_month_revenue : null;
  return (
    <span className="block space-y-1.5">
      <span className="block h-1.5 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        <span className={`block h-full rounded-full ${achieved >= 100 ? 'bg-green-600' : achieved >= 90 ? 'bg-blue-600' : 'bg-amber-500'}`} style={{ width: `${achieved}%` }} />
      </span>
      <span className="block">{shortMoney(s.target_actual)} of {shortMoney(s.target_revenue)} target for this period</span>
      {s.target_projection != null && s.target_month_revenue != null && (
        <span className={`block font-medium ${onTrack ? 'text-green-700' : 'text-amber-700'}`} data-testid="sales-target-pace">
          On pace for {shortMoney(s.target_projection)} of {shortMoney(s.target_month_revenue)} in {monthName(s.target_month)}
        </span>
      )}
    </span>
  );
};

export const SalesKpiRow = ({ summary, trend = [], compareLabel, metric = 'revenue', onMetric, onStock, onReturns, loading = false, hasListFilter = false }) => {
  const s = summary || {};
  const ready = Boolean(summary);
  const cover = s.stock_cover_days;
  const tone = coverTone(cover);
  return (
    <div className="grid grid-cols-1 gap-3 min-[420px]:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5" data-testid="sales-kpi-grid">
      <KpiCard label="Revenue" testId="sales-kpi-revenue" loading={loading} compareLabel={compareLabel}
        value={ready ? <span title={wholeMoney(s.revenue)}>{shortMoney(s.revenue)}</span> : null}
        current={s.revenue} previous={ready ? s.previous_revenue : null}
        sparkline={trend} sparkKey="revenue" sparkCompareKey="previous_revenue"
        onClick={() => onMetric?.('revenue')} active={metric === 'revenue'}
        footnote={!ready ? null : (
          <span className="block space-y-0.5">
            {s.returned_value > 0 && (
              <span className="block font-medium text-gray-900" data-testid="sales-kpi-after-returns">
                After returns {shortMoney(s.revenue_after_returns)} <span className="font-normal text-gray-500">(−{shortMoney(s.returned_value)})</span>
              </span>
            )}
            {s.avg_selling_price != null && <span className="block">Avg price {wholeMoney(s.avg_selling_price)}{s.previous_avg_selling_price != null ? ` (was ${wholeMoney(s.previous_avg_selling_price)})` : ''}</span>}
          </span>
        )}
        hint="Units sold × recorded selling price, before fees. 'After returns' deducts returns on Amazon Seller API sales (refunded amount, else the sale price), from the returns go-live month. Targets use the figure before returns." />
      <KpiCard label="Units sold" kind="number" testId="sales-kpi-units" loading={loading} compareLabel={compareLabel}
        value={ready ? number(s.units) : null} current={s.units} previous={ready ? s.previous_units : null}
        sparkline={trend} sparkKey="units" sparkCompareKey="previous_units" sparkColor="#059669"
        onClick={() => onMetric?.('units')} active={metric === 'units'} />
      <KpiCard label="Returns" kind="number" invert noun="returns" testId="sales-kpi-returns" loading={loading} compareLabel={compareLabel}
        value={ready ? <span>{number(s.returned_units)} <span className="text-sm font-normal text-gray-500">units</span></span> : null}
        current={s.returned_units} previous={ready ? s.previous_returned_units : null}
        sparkline={trend} sparkKey="returns" sparkCompareKey="previous_returns" sparkColor="#C2410C"
        onClick={() => { onMetric?.('returns'); onReturns?.(); }} active={metric === 'returns'}
        footnote={ready ? `${pct(s.return_rate)} of units sold${s.previous_return_rate != null ? ` (was ${pct(s.previous_return_rate)})` : ''}${onReturns ? ' · click for why and where' : ''}` : null}
        hint="Returns recorded by return date in this period; they may relate to earlier sales. Click for reasons, condition, problem SKUs and the rate by sale cohort." />
      <KpiCard label="Target" testId="sales-kpi-target" loading={loading}
        value={!ready ? null : s.target_revenue == null
          ? <span className="text-base font-medium text-gray-500">No target set</span>
          : <span>{pct(s.target_achievement)} <span className="text-sm font-normal text-gray-500">achieved</span></span>}
        footnote={ready ? <TargetFootnote s={s} hasListFilter={hasListFilter} /> : null} />
      <KpiCard label="Stock cover" testId="sales-kpi-cover" loading={loading} onClick={onStock}
        hint="Days the current stock of this period's top 10 SKUs lasts at their average daily sales (revenue-weighted)."
        value={!ready ? null : cover == null
          ? <span className="text-base font-medium text-gray-500">Not known</span>
          : <span className={COVER_TEXT[tone]}>{number(Math.round(cover))} <span className="text-sm font-normal text-gray-500">days</span></span>}
        footnote={!ready ? null : (
          <span className={s.low_cover_skus ? 'font-medium text-red-700' : ''}>
            {s.low_cover_skus
              ? `${number(s.low_cover_skus)} best-seller${s.low_cover_skus === 1 ? '' : 's'} under 15 days of cover`
              : 'No best-seller under 15 days of cover'}
            <span className="block font-normal text-blue-700">Open stock health →</span>
          </span>
        )} />
    </div>
  );
};

export const SalesMetrics = SalesKpiRow;
export default SalesKpiRow;
